export interface WorkflowModuleOperationCatalogItem {
  moduleKey: string;
  operationKey: string;
  displayName: string;
  inputSchemaName?: string | null;
  outputSchemaName?: string | null;
  requiredPermissions: string[];
  supportsCompensation: boolean;
  compensationOperationKey?: string | null;
}

export interface WorkflowApiConnectorOperationCatalogItem {
  operationKey: string;
  displayName: string;
  httpMethod: string;
  pathTemplate: string;
}

export interface WorkflowApiConnectorCatalogItem {
  connectorKey: string;
  displayName: string;
  authMode: string;
  requiredPermissions: string[];
  operations: WorkflowApiConnectorOperationCatalogItem[];
}

export interface WorkflowActionCatalogApiItem {
  actionKey: string;
  variableKey: string;
  variableValue: string;
  labelKey?: string | null;
  suggestedConditionExpression: string;
}

export interface WorkflowTriggerSubscriptionItem {
  processDefinitionId: string;
  processDefinitionName: string;
  triggerId: string;
  triggerName: string;
  triggerType: string;
  configurationJson: string;
}

export interface WorkflowRetryPolicyConfig {
  maxAttempts?: number;
  backoffSeconds?: number[];
}

export interface WorkflowServiceOperatorConfig {
  moduleKey?: string;
  operationKey?: string;
  inputMapping?: Record<string, string>;
  outputMapping?: Record<string, string>;
  retryPolicy?: WorkflowRetryPolicyConfig;
  compensation?: {
    operationKey?: string;
    inputMapping?: Record<string, string>;
    outputMapping?: Record<string, string>;
  };
}

export interface WorkflowApiCallOperatorConfig {
  connectorKey?: string;
  operationKey?: string;
  timeoutSeconds?: number;
  inputMapping?: Record<string, string>;
  outputMapping?: Record<string, string>;
  retryPolicy?: WorkflowRetryPolicyConfig;
}

export interface WorkflowAiOperatorConfig {
  useCaseKey?: string;
  moduleKey?: string;
  promptRef?: string;
  outputSchema?: string;
  minConfidence?: number;
  onLowConfidence?: string;
  inputMapping?: Record<string, string>;
  outputMapping?: Record<string, string>;
  retryPolicy?: WorkflowRetryPolicyConfig;
}

export interface WorkflowTriggerOperatorConfig {
  eventTopic?: string;
  messageTopic?: string;
  aiSignalType?: string;
  intervalMinutes?: number;
  variableMapping?: Record<string, string>;
}

export interface WorkflowOutgoingFlowOption {
  id: string;
  name: string;
  bpmnElementId: string;
}

/** Align with backend `WorkflowAiUseCasesContributor` until a catalog API exists. */
export const WORKFLOW_AI_USE_CASE_OPTIONS = [
  {
    useCaseKey: 'workflow.sales.intent.extract',
    displayName: 'Extract sales intent from voice or transcript',
    outputSchema: 'SalesVoiceIntentV1'
  },
  {
    useCaseKey: 'workflow.generic.decision.support',
    displayName: 'Generic workflow decision support',
    outputSchema: 'WorkflowDecisionSupportV1'
  }
] as const;

export const WORKFLOW_SYSTEM_TASK_MODULE_KEYS = ['workflow-bpms', 'platform-core'] as const;

export interface ModuleSurfaceOperatorConfig {
  moduleKey: string;
  surfaceKey: string;
  surfaceMode?: string;
  contextMapping: Record<string, string>;
  outputMapping?: Record<string, string>;
  allowFullPageEscape: boolean;
  fullPageRouteTemplate: string;
}

export function defaultModuleSurfaceOperatorConfig(): ModuleSurfaceOperatorConfig {
  return {
    moduleKey: '',
    surfaceKey: '',
    surfaceMode: '',
    contextMapping: {},
    outputMapping: {},
    allowFullPageEscape: true,
    fullPageRouteTemplate: ''
  };
}

export interface WorkflowModuleSurfaceCatalogItem {
  moduleKey: string;
  surfaceKey: string;
  titleKey: string;
  descriptionKey?: string | null;
  fullPageRouteTemplate?: string | null;
}

export const WORKFLOW_DESIGNER_MODULE_SURFACE_MODULE_KEYS = ['platform', 'growth'] as const;

export const WORKFLOW_AI_LOW_CONFIDENCE_POLICIES = [
  { value: 'fail', labelKey: 'workflow.designer.operators.ai.onLowConfidence.fail' },
  { value: 'setVariables', labelKey: 'workflow.designer.operators.ai.onLowConfidence.setVariables' },
  { value: 'routeToReview', labelKey: 'workflow.designer.operators.ai.onLowConfidence.routeToReview' }
] as const;
